"use client";

import React, { useRef, useState, useEffect } from "react";

export default function EnrollSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    course: "",
    age: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el) => {
              el.classList.add("revealed");
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to backend/email service
    setSubmitted(true);
  };

  return (
    <section
      id="enroll"
      ref={sectionRef}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(242,201,76,0.08) 0%, transparent 65%), #111111",
      }}
    >
      <div className="gold-divider mb-0 absolute top-0 left-0 right-0" />

      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14 reveal-hidden">
          <span className="badge-yellow px-3 py-1 rounded-full text-xs font-display font-bold uppercase tracking-widest">
            Get Started
          </span>
          <h2
            className="font-display font-black mt-4 leading-tight tracking-tight"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            Ready to Start Your
            <br />
            <span className="text-yellow">Musical Journey?</span>
          </h2>
          <p
            className="mt-4 text-base md:text-lg max-w-xl mx-auto leading-relaxed"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            Book a free trial class today. No prior experience needed — just bring your
            enthusiasm and we'll handle the rest.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left: Info */}
          <div className="lg:col-span-2 space-y-6 reveal-hidden">
            <div>
              <h3 className="font-display font-bold text-lg text-yellow mb-2">
                What You Get
              </h3>
              <ul className="space-y-3">
                {[
                  "Free 30-minute trial class",
                  "Meet your instructor personally",
                  "Tour of our studio space",
                  "Custom learning plan discussion",
                  "No obligation to enroll",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
                    <span className="text-yellow mt-0.5 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="rounded-2xl p-5 stat-card"
            >
              <p className="text-xs font-display font-bold uppercase tracking-widest mb-2" style={{ color: "#F2C94C" }}>
                🎵 Quick Enquiry
              </p>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
                Prefer to call?
              </p>
              <a
                href="tel:+919849007187"
                className="contact-link font-display font-bold text-lg text-white block mt-1"
              >
                +91 98490 07187
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="lg:col-span-3 reveal-hidden stagger-2 rounded-3xl p-8 md:p-10"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {submitted ? (
              <div className="text-center py-10">
                <div className="text-5xl mb-4">🎸</div>
                <h3 className="font-display font-black text-2xl text-yellow mb-2">
                  You're In!
                </h3>
                <p className="text-base" style={{ color: "rgba(255,255,255,0.6)" }}>
                  We'll call you within 24 hours to schedule your free trial class.
                  Get ready to jam!
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-display font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Arjun Sharma"
                      className="form-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-display font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 98765 43210"
                      className="form-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-display font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="arjun@example.com"
                    className="form-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-display font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                      Course of Interest *
                    </label>
                    <select
                      name="course"
                      value={form.course}
                      onChange={handleChange}
                      required
                      className="form-input w-full px-4 py-3 rounded-xl text-sm"
                      style={{ appearance: "none" }}
                    >
                      <option value="" disabled>Select a course</option>
                      <option value="guitar">🎸 Guitar</option>
                      <option value="vocals">🎤 Vocals</option>
                      <option value="art">🎨 Art & Craft</option>
                      <option value="jamming">🥁 Jamming Sessions</option>
                      <option value="other">Other / Unsure</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-display font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                      Age Group
                    </label>
                    <select
                      name="age"
                      value={form.age}
                      onChange={handleChange}
                      className="form-input w-full px-4 py-3 rounded-xl text-sm"
                      style={{ appearance: "none" }}
                    >
                      <option value="" disabled>Select age group</option>
                      <option value="under-12">Under 12</option>
                      <option value="13-18">13 – 18 (School)</option>
                      <option value="19-25">19 – 25 (College)</option>
                      <option value="26-35">26 – 35 (Young Adult)</option>
                      <option value="35+">35+ (Adult)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-display font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                    Message (Optional)
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Tell us about your experience level or any questions..."
                    className="form-input w-full px-4 py-3 rounded-xl text-sm resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-yellow w-full py-4 rounded-xl text-base font-display font-black tracking-wide"
                >
                  🎵 Join the Jam — Book Free Trial
                </button>

                <p className="text-xs text-center" style={{ color: "rgba(255,255,255,0.3)" }}>
                  By submitting, you agree to be contacted by Hi-Hats Rajanukunte regarding your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}